import { useState } from "react"
import { UserPlus, X } from "lucide-react"
import PeoplePickerPopover from "@/components/PeoplePickerPopover"
import { Avatar } from "@/components/ui/Avatar"
import { Button } from "@/components/ui/Button"
import { DatePicker } from "@/components/ui/DatePicker"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select"
import type { Person } from "@/lib/person"
import { cn } from "@/lib/utils"
import type { Channel, ReviewAction } from "../types"
import MentionInput from "./MentionInput"

// What the editor hands back on Save.
export interface ActionDraft {
  action_type: string
  channel_node_id: string | null // null means all channels
  starts_on: string | null
  ends_on: string | null
  note: string
  assignee_ids: string[]
}

const ALL_CHANNELS = "all"

interface Props {
  action: ReviewAction | null
  actionTypes: string[]
  channels: Channel[]
  people: Person[]
  saving: boolean
  onSave: (draft: ActionDraft) => void
  // Left out for the always-open blank form, which has nothing to cancel.
  onCancel?: () => void
  className?: string
}

// The options on one line (type, channel, dates, people in charge), the note
// underneath with room for several lines, Save at the bottom right.
// Everything except the type is optional. A one-day action has the same From
// and To.
export default function ActionEditor({ action, actionTypes, channels, people, saving, onSave, onCancel, className }: Props) {
  const [type, setType] = useState(action?.action_type ?? "")
  const [note, setNote] = useState(action?.note ?? "")
  const [channel, setChannel] = useState(action ? (action.channel_id ?? ALL_CHANNELS) : "")
  const [from, setFrom] = useState<string | null>(action?.starts_on ?? null)
  const [to, setTo] = useState<string | null>(action?.ends_on ?? null)
  const [assignees, setAssignees] = useState<string[]>(action?.assignee_ids ?? [])
  const chosen = people.filter((p) => assignees.includes(p.id))

  const save = () => {
    if (!type || saving) return
    onSave({
      action_type: type,
      channel_node_id: channel && channel !== ALL_CHANNELS ? channel : null,
      starts_on: from,
      ends_on: to ?? from,
      note: note.trim(),
      assignee_ids: assignees,
    })
  }

  return (
    <div className={cn("space-y-2 bg-muted/50 p-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-52 bg-background font-medium">
            <SelectValue placeholder="Pick a type" />
          </SelectTrigger>
          <SelectContent>
            {actionTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-40 bg-background">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CHANNELS}>All channels</SelectItem>
            {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <DatePicker
            value={from ?? undefined}
            onChange={(v) => { setFrom(v); if (to && to < v) setTo(v) }}
            placeholder="From"
            className="w-36"
          />
          <span>–</span>
          <DatePicker value={to ?? undefined} onChange={setTo} placeholder="To" className="w-36" fromDate={from ?? undefined} />
          {(from || to) && (
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => { setFrom(null); setTo(null) }} aria-label="Clear dates">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <PeoplePickerPopover title="People in charge" people={people} selectedIds={assignees} onChange={setAssignees}>
          <Button variant="outline" className="h-9 gap-1.5 bg-background px-3 font-normal">
            {chosen.length === 0 ? (
              <><UserPlus className="h-4 w-4" /> People in charge</>
            ) : (
              <>
                <span className="flex -space-x-1">
                  {chosen.map((p) => (
                    <Avatar key={p.id} name={p.name} initials={p.initials} avatarUrl={p.avatar_url} tone="primary" className="ring-2 ring-background" />
                  ))}
                </span>
                <span>{chosen.length === 1 ? chosen[0].name.split(" ")[0] : `${chosen.length} people`}</span>
              </>
            )}
          </Button>
        </PeoplePickerPopover>
      </div>
      <MentionInput value={note} onChange={setNote} onSubmit={save} placeholder="Note. Type # to link a product" />
      <div className="flex items-center justify-end gap-2">
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
        <Button size="sm" onClick={save} disabled={!type || saving}>Save</Button>
      </div>
    </div>
  )
}
